import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Theme } from './ThemeSelectionDialog';
import { useLanguage } from '@/i18n/LanguageContext';

interface ThemeSectionProps {
  selectedTheme: Theme | null;
  onThemeChange: () => void;
}

export function ThemeSection({ selectedTheme, onThemeChange }: ThemeSectionProps) {
  const { t } = useLanguage();

  return (
    <Card>
      <CardContent className="p-6 pt-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">{t('course-content.detail.theme')}</h2>
            {selectedTheme ? (
              <p className="text-sm text-gray-500 mt-1">{selectedTheme.name}</p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">{t('course-content.detail.noThemeSelected')}</p>
            )}
          </div>
          <Button variant="outline" onClick={onThemeChange}>
            {t('course-content.detail.changeTheme')}
          </Button>
        </div>

        {/* Theme Preview Section */}
        <div>
          <h3 className="text-sm font-medium text-gray-500 mb-2">{t('course-content.detail.themePreview')}</h3>
          {selectedTheme ? (
            <div className="rounded-md border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="mb-4">
                <h3 className="text-lg font-medium" style={{ fontFamily: selectedTheme.styles.fontFamily }}>
                  Sample Lesson Title
                </h3>
                <p className="mt-2 text-gray-600" style={{ fontFamily: selectedTheme.styles.fontFamily }}>
                  This is a preview of how your course content will look with the selected theme.
                </p>
              </div>
              
              <div className="space-y-2">
                <Button 
                  variant="primary"
                  style={{ 
                    backgroundColor: selectedTheme.styles.primaryColor,
                    borderRadius: selectedTheme.styles.borderRadius
                  }}
                >
                  Primary Button
                </Button>
                <Button 
                  variant="outline"
                  style={{ 
                    borderColor: selectedTheme.styles.secondaryColor,
                    color: selectedTheme.styles.secondaryColor,
                    borderRadius: selectedTheme.styles.borderRadius
                  }}
                >
                  Secondary Button
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-6 text-center dark:border-gray-700 dark:bg-gray-800">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('course-content.detail.selectThemeToPreview')}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
} 